import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Target, Globe, Award, Zap, CheckCircle2 } from 'lucide-react';
import { investorData } from '../data/mock';

const Investors = () => {
  return (
    <div className="page-container">
      {/* Hero Section */}
      <section className="investors-hero">
        <div className="investors-hero-content">
          <Badge className="hero-badge">Investment Opportunity</Badge>
          <h1 className="investors-hero-title">
            Building the Future of AI Security
          </h1>
          <p className="investors-hero-description">
            IrisTechLabs is positioned at the intersection of three massive trends: AI adoption, cybersecurity threats, and regulatory compliance. 
            We're building the AI-first security platform for the next decade.
          </p>
        </div>
      </section>

      {/* Market Opportunity */}
      <section className="section market-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Market Opportunity</h2>
            <p className="section-description">
              Massive addressable market with strong tailwinds
            </p>
          </div>
          <div className="market-grid">
            <Card className="market-card">
              <CardHeader>
                <TrendingUp className="market-icon" />
                <CardTitle className="market-metric">{investorData.marketOpportunity.globalCybersecurityMarket}</CardTitle>
                <CardDescription>Global Cybersecurity Market</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Rapidly expanding market driven by digital transformation</p>
              </CardContent>
            </Card>
            <Card className="market-card">
              <CardHeader>
                <Zap className="market-icon" />
                <CardTitle className="market-metric">{investorData.marketOpportunity.aiSecurityMarket}</CardTitle>
                <CardDescription>AI Security Market</CardDescription>
              </CardHeader>
              <CardContent>
                <p>AI security emerging as critical category</p>
              </CardContent>
            </Card>
            <Card className="market-card">
              <CardHeader>
                <Globe className="market-icon" />
                <CardTitle className="market-metric">{investorData.marketOpportunity.indiaMarketGrowth}</CardTitle>
                <CardDescription>India Market CAGR</CardDescription>
              </CardHeader>
              <CardContent>
                <p>India's fastest-growing cybersecurity market</p>
              </CardContent>
            </Card>
            <Card className="market-card">
              <CardHeader>
                <Target className="market-icon" />
                <CardTitle className="market-metric">{investorData.marketOpportunity.cagr}</CardTitle>
                <CardDescription>Global Market CAGR</CardDescription>
              </CardHeader>
              <CardContent>
                <p>Sustained double-digit growth trajectory</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Now */}
      <section className="section why-now-section">
        <div className="section-content">
          <div className="why-now-grid">
            <div className="why-now-text">
              <h2 className="why-now-title">Why Now?</h2>
              <p className="why-now-description">
                Three converging trends create unprecedented opportunity
              </p>
              <div className="why-now-list">
                <div className="why-now-item">
                  <div className="why-now-number">1</div>
                  <div>
                    <h3 className="why-now-item-title">AI Adoption Surge</h3>
                    <p className="why-now-item-text">
                      Enterprises racing to adopt AI, creating massive new attack surface and governance challenges
                    </p>
                  </div>
                </div>
                <div className="why-now-item">
                  <div className="why-now-number">2</div>
                  <div>
                    <h3 className="why-now-item-title">Regulatory Tailwind</h3>
                    <p className="why-now-item-text">
                      DPDP India, GDPR, DORA driving compliance spending. Gartner mandating CTEM for enterprises
                    </p>
                  </div>
                </div>
                <div className="why-now-item">
                  <div className="why-now-number">3</div>
                  <div>
                    <h3 className="why-now-item-title">India Digital Economy</h3>
                    <p className="why-now-item-text">
                      India's digital economy growing 15% CAGR, BFSI sector digitizing rapidly, creating security demand
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="why-now-image">
              <img 
                src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHx0ZWFtJTIwY29sbGFib3JhdGlvbnxlbnwwfHx8fDE3NzIxMzY4NzB8MA&ixlib=rb-4.1.0&q=85" 
                alt="Team Collaboration" 
                className="why-now-img"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Competitive Advantages */}
      <section className="section advantages-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Competitive Advantages</h2>
            <p className="section-description">
              What makes IrisTechLabs different
            </p>
          </div>
          <div className="advantages-grid">
            {investorData.competitiveAdvantages.map((advantage, index) => (
              <Card key={index} className="advantage-card">
                <CardContent className="advantage-content">
                  <CheckCircle2 className="advantage-icon" />
                  <p className="advantage-text">{advantage}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Expansion Roadmap */}
      <section className="section roadmap-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Expansion Roadmap</h2>
            <p className="section-description">
              Strategic growth plan for next 5 years
            </p>
          </div>
          <div className="roadmap-timeline">
            {investorData.expansionRoadmap.map((phase, index) => (
              <Card key={index} className="roadmap-card">
                <CardHeader>
                  <Badge className="roadmap-badge">{phase.phase}</Badge>
                  <CardTitle>{phase.focus}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="roadmap-target">Target: {phase.target}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AI Differentiation */}
      <section className="section ai-diff-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">AI Differentiation</h2>
            <p className="section-description">
              Not AI-washed, truly AI-native
            </p>
          </div>
          <div className="ai-diff-grid">
            <Card className="ai-diff-card">
              <CardHeader>
                <CardTitle>Traditional Security</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="comparison-list">
                  <li>Rule-based detection</li>
                  <li>High false positive rates</li>
                  <li>Manual threat hunting</li>
                  <li>Reactive approach</li>
                  <li>Annual pentesting</li>
                </ul>
              </CardContent>
            </Card>
            <Card className="ai-diff-card highlight">
              <CardHeader>
                <CardTitle>IrisTechLabs AI-First</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="comparison-list">
                  <li><CheckCircle2 className="list-check" /> ML-powered correlation</li>
                  <li><CheckCircle2 className="list-check" /> 70% false positive reduction</li>
                  <li><CheckCircle2 className="list-check" /> Automated threat hunting</li>
                  <li><CheckCircle2 className="list-check" /> Predictive intelligence</li>
                  <li><CheckCircle2 className="list-check" /> Continuous validation</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Product Flywheel */}
      <section className="section flywheel-section">
        <div className="section-content">
          <div className="section-header">
            <h2 className="section-title">Product Flywheel</h2>
            <p className="section-description">
              AI models improve with scale, creating defensible moat
            </p>
          </div>
          <div className="flywheel-container">
            <div className="flywheel-center">
              <Award className="flywheel-icon" />
              <p className="flywheel-center-text">Continuous Improvement</p>
            </div>
            <div className="flywheel-items">
              {investorData.productFlywheel.map((item, index) => (
                <div key={index} className="flywheel-item">
                  <div className="flywheel-step">{index + 1}</div>
                  <p className="flywheel-text">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Interested in Learning More?</h2>
          <p className="cta-description">
            Connect with our team to discuss investment opportunities
          </p>
          <Button asChild size="lg" className="cta-button-primary">
            <Link to="/contact">Contact Investor Relations</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Investors;
